import { Injectable } from '@angular/core';
import { translate } from '@ngneat/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { createEntityAdapter } from '@ngrx/entity';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, delay, map, mergeMap, withLatestFrom } from 'rxjs/operators';

import { ApiService } from '../../services/api/api.service';
import { AlertError, AlertSuccess, ResetBundles, SelectBundle } from '../actions';
import { SetAppBridgeNodes, SetAppHelpDocumentation } from '../actions/apps.actions';
import * as actions from '../actions/manage-apps.actions';
import { SetCurrentOrganization } from '../actions/session.actions';
import {
    getAllServicesByOrgId$,
    getBridgeIdsVsMetadataIds$,
    getCurrentOrganizationId$,
    getIsGlobalRepository$,
    getManageAppsAssignableSubscriptionIdsByBundleId$,
    getManageAppsBundleSubscribedAppIds$,
    getManageAppsOrganizationEntity$,
    getManageAppsPageStatus$,
    getManageAppsSelectedBundleId$,
    getManageAppsSelectedOrgAndAppIds$,
    getManageAppsSelectedServicesIds$,
    getManageAppsSubscriptionStatus$,
    getOnlySubscribedApps$,
    getSelectedOrgIdsArray$,
    getSelectedServiceIdsArray$,
    getServicesByStatus$,
    getSubscriptionApprovalCount$,
    getSubscriptionPagiantion$,
    getSubscriptionServicesByPagination$,
} from '../selectors';
import { manageAppsAdapters, ManageAppsOrganizationEntity, Service } from '../states';

@Injectable()
export class ManageAppsEffects {
    constructor(private actions$: Actions, private _api: ApiService, private store$: Store) {}

    setCurrentOrganization$ = createEffect(() => {
        return this.actions$.pipe(
            delay(10),
            ofType(SetCurrentOrganization, actions.ManageAppsSetOrganization),
            withLatestFrom(this.store$.select(getManageAppsOrganizationEntity$)),
            mergeMap(([action, organizationEntity]) => {
                if (organizationEntity || !action?.organization?.id) {
                    return [];
                }
                const servicesAdapter = createEntityAdapter<Service>({
                    selectId: service => service.serviceId,
                });
                const servicesEntity: ManageAppsOrganizationEntity = {
                    id: action.organization?.id,
                    services: servicesAdapter.getInitialState(),
                };
                manageAppsAdapters[action.organization.id] = servicesAdapter;
                return of(
                    actions.SetManageAppsOrganizationEntity({
                        servicesEntity,
                        organizationsAdapter: manageAppsAdapters.organizations,
                    })
                );
            })
        );
    });

    setAppsCount$ = createEffect(() => {
        return this.actions$.pipe(
            delay(10),
            ofType(actions.SetManageAppsStatus, actions.ManageAppsSetOrgsAndApps, actions.ManageAppsUpdateMany),
            withLatestFrom(
                this.store$.select(getManageAppsOrganizationEntity$),
                this.store$.select(getManageAppsPageStatus$),
                this.store$.select(getManageAppsSubscriptionStatus$),
                this.store$.select(getManageAppsSelectedOrgAndAppIds$),
                this.store$.select(getSubscriptionApprovalCount$)
            ),
            mergeMap(([_action, organizationEntity, pagination, status, { orgIdArray, itemIdArray }, count]) => {
                if (!pagination.getByPagination || count > 0) {
                    return [];
                }
                return from(
                    this._api.subscriptions.subscribedAssetsCount({
                        organizationId: organizationEntity?.id,
                        subscriptionType: 'SUBSCRIPTION_APPROVAL',
                        status: status,
                        orgIdArray: orgIdArray,
                        itemIdArray: itemIdArray,
                    })
                ).pipe(
                    map(response => actions.ManageAppsSetCount({ count: response as number })),
                    catchError(errorResponse => of(AlertError({ message: errorResponse })))
                );
            })
        );
    });

    updateAssetHelpDocument$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetAppHelpDocumentation),
            withLatestFrom(this.store$.select(getManageAppsSubscriptionStatus$)),
            mergeMap(([action, status]) => {
                if (action.bridgeServiceId) {
                    return of(
                        actions.UpdateManageAppsBridgeNode({
                            serviceId: action.serviceId,
                            status: status,
                            property: 'helpDocuments',
                            propertyValue: action.helpDocuments,
                            bridgeNodeId: action.bridgeNodeId,
                        })
                    );
                }
                return of(
                    actions.UpdateAssetDetails({
                        serviceId: action.serviceId,
                        status: status,
                        property: 'helpDocuments',
                        propertyValue: action.helpDocuments,
                    })
                );
            })
        );
    });

    setBridgeNodes$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SetAppBridgeNodes),
            withLatestFrom(this.store$.select(getManageAppsOrganizationEntity$)),
            mergeMap(([action]) => {
                if (!action.assetId) {
                    return [];
                }
                return of(
                    actions.UpdateServicesWithBridgeNodes({
                        bridgeNodes: action.bridgeNodes,
                        itemId: action.assetId,
                        serviceId: action.serviceId,
                    })
                );
            })
        );
    });

    getAssignableSubscriptionIds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetManageAppsAssignableSubscriptionIds, SelectBundle),
            delay(10),
            withLatestFrom(
                this.store$.select(getManageAppsAssignableSubscriptionIdsByBundleId$),
                this.store$.select(getManageAppsSelectedBundleId$)
            ),
            mergeMap(([_action, subscriptionIds, bundleId]) => {
                if (subscriptionIds?.length > 0 || !bundleId) {
                    return [];
                }
                return from(this._api.assets.getAssignableSubscriptions(bundleId)).pipe(
                    map(response => {
                        return actions.SetManageAppsAssignableSubscriptionIds({ ids: response as string[] });
                    }),
                    catchError(error => of(AlertError({ message: error })))
                );
            })
        );
    });

    getBundleSubscribedApps$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(SelectBundle),
            delay(10),
            withLatestFrom(this.store$.select(getManageAppsBundleSubscribedAppIds$)),
            mergeMap(([action, bundleAppIds]) => {
                if (!action.id || bundleAppIds.length > 0) {
                    return of(
                        actions.SetManageAppsBundleSubscriptionIds({
                            ids: bundleAppIds,
                        })
                    );
                }
                return from(this._api.assets.getBundleSubscriptions(action.id)).pipe(
                    map(response => {
                        return actions.SetManageAppsBundleSubscriptionIds({
                            ids: response as string[],
                        });
                    }),
                    catchError(error => of(AlertError({ message: error })))
                );
            })
        );
    });

    updateManageAppsSelections$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(
                actions.SetManageAppsBundleSubscriptionIds,
                actions.SetManageAppsStatus,
                actions.SetManageAppsResponse,
                actions.SelectManageAppsServices,
                ResetBundles
            ),
            withLatestFrom(this.store$.select(getManageAppsSelectedServicesIds$)),
            mergeMap(([action, serviceIds]) => {
                return of(
                    actions.SetManageAppsSelectedServiceIds({
                        ids: action['ids'] || serviceIds,
                    })
                );
            })
        );
    });

    SubmitAppStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ManageAppsSubmitAppStatus),
            mergeMap(action => {
                return this._api.subscriptions.updateSubscribedServices(action.payload).pipe(
                    map(() => {
                        this.store$.dispatch(
                            actions.SetSubscriptionAppsByStatus({
                                resetSubscriptions: true,
                                organizationId: action.payload.orgId,
                                status: action.payload.status === 'ACTIVATE' ? 'APPROVED' : action.payload.status,
                                services: [],
                            })
                        );
                        this.store$.dispatch(AlertSuccess({ message: this.getMessage(action.payload.status) }));
                        return actions.UpdateSubscribedServicesSuccess();
                    }),
                    catchError(res => {
                        return of(AlertError({ message: res }));
                    })
                );
            })
        );
    });

    private getMessage = (action: string) => {
        switch (action) {
            case 'APPROVED':
                return translate('Subscription approved');
            case 'REJECTED':
                return translate('Subscription rejected');
            case 'PENDING_DEACTIVATE':
                return translate('Subscription deactivated');
            case 'ACTIVATE':
                return translate('Subscription activated');
        }
    };

    getAllApps$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAllApps),
            delay(100),
            withLatestFrom(
                this.store$.select(getAllServicesByOrgId$),
                this.store$.select(getManageAppsPageStatus$),
                this.store$.select(getCurrentOrganizationId$)
            ),
            mergeMap(([action, services, pagination, orgId]) => {
                if (pagination.getByPagination) {
                    return [];
                }
                if (services?.length > 0 && !action.forceFetch) {
                    return [];
                }
                const apiCall =
                    action.isPackagePage || action?.allApps
                        ? from(this._api.subscriptions.getAllServicesOfCurrentProvider())
                        : from(this._api.subscriptions.getAllServices(undefined, action?.considerGlobalRepository));
                return apiCall.pipe(
                    map(response =>
                        actions.SetAllApps({
                            organizationId: orgId,
                            services: response,
                            isGlobalRepository: action?.considerGlobalRepository,
                            allApps: action.allApps,
                        })
                    ),
                    catchError(() => [])
                );
            })
        );
    });

    getAppsByStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetAppsbyStatus),
            delay(10),
            withLatestFrom(
                this.store$.select(getServicesByStatus$),
                this.store$.select(getManageAppsPageStatus$),
                this.store$.select(getManageAppsSubscriptionStatus$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getOnlySubscribedApps$)
            ),
            mergeMap(([_action, services, pagination, status, orgId, subscribedApps]) => {
                if (pagination.getByPagination) {
                    return [];
                }
                if (services?.length > 0) {
                    return [];
                }
                const suspendedServiceIds = new Set(subscribedApps.filter(app => app.suspended).map(app => app.serviceId));
                return from(
                    this._api.subscriptions.getServicesByStatus({
                        pageIndex: 1,
                        status: status,
                        size: 200,
                        noVariableLoading: true,
                        orgIdArray: [orgId],
                    })
                ).pipe(
                    map(response =>
                        actions.SetAppsByStatus({
                            organizationId: orgId,
                            status: status,
                            services: (response as any[])?.map(service => ({
                                ...service,
                                suspended: suspendedServiceIds.has(service.serviceId),
                            })),
                        })
                    ),
                    catchError(() => [])
                );
            })
        );
    });

    getSubscriptionServicesByStatus$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.GetSubscriptionServicesByStatus),
            withLatestFrom(
                this.store$.select(getManageAppsSubscriptionStatus$),
                this.store$.select(getCurrentOrganizationId$),
                this.store$.select(getSubscriptionServicesByPagination$),
                this.store$.select(getSubscriptionPagiantion$),
                this.store$.select(getSelectedOrgIdsArray$),
                this.store$.select(getSelectedServiceIdsArray$),
                this.store$.select(getOnlySubscribedApps$)
            ),
            mergeMap(([_action, status, orgId, services, pagination, orgIds, serviceIds, subscribedApps]) => {
                if (services?.length > 0 && services?.length >= pagination.pageSize * (pagination.pageIndex + 1)) {
                    return [];
                }
                const suspendedServiceIds = new Set(subscribedApps.filter(app => app.suspended).map(app => app.serviceId));
                return from(
                    this._api.subscriptions.getSubcriptionServices({
                        pageIndex: pagination.pageIndex + 1,
                        status: status,
                        size: pagination.pageSize,
                        serviceIdArray: serviceIds,
                        orgIdArray: orgIds,
                    })
                ).pipe(
                    map((response: any) => {
                        this.store$.dispatch(
                            actions.setSubscriptionsCount({
                                count: response?.count,
                                status: status,
                            })
                        );
                        return actions.SetSubscriptionAppsByStatus({
                            organizationId: orgId,
                            status: status,
                            services: response.services?.map(service => ({
                                ...service,
                                suspended: suspendedServiceIds.has(service.serviceId),
                            })),
                        });
                    }),
                    catchError(() => [])
                );
            })
        );
    });

    submitManageAppsSelectedServiceIds$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.SubmitManageAppsSelectedServiceIds),
            withLatestFrom(this.store$.select(getIsGlobalRepository$)),
            mergeMap(([action, isGlobalRepository]: [any, boolean]) => {
                return this._api.subscriptions
                    .subscribeToServices({
                        serviceIds: action.ids,
                        organizationIds: action.orgIds,
                    })
                    .pipe(
                        map(() => {
                            this.store$.dispatch(AlertSuccess({ message: translate('Subscription request placed successfully') }));
                            return [
                                actions.UpdateSubscribedServices({
                                    ids: action.ids,
                                }),
                                actions.GetAllApps({
                                    isPackagePage: false,
                                    considerGlobalRepository: isGlobalRepository,
                                    forceFetch: true,
                                }),
                            ];
                        }),
                        mergeMap(actionsArray => actionsArray),
                        catchError(res => {
                            return of(AlertError({ message: res }));
                        })
                    );
            })
        );
    });

    validateAndSubscribeToConnector$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(actions.ValidateAndSubscribetoConnector),
            withLatestFrom(this.store$.select(getIsGlobalRepository$)),
            mergeMap(([action, isGlobalRepository]: [any, boolean]) => {
                return this._api.subscriptions
                    .validateAndsubscribeToConnector({
                        resourceName: action.resourceName,
                        organizationIds: action.orgIds,
                        requiredFields: action.requiredFields,
                    })
                    .pipe(
                        map(res => {
                            this.store$.dispatch(AlertSuccess({ message: translate('Subscription request placed successful') }));
                            res?.authorizationURI?.length > 0 &&
                                this.store$.dispatch(
                                    actions.ValidateConnetorSuccessAction({
                                        authorizationURI: res.authorizationURI,
                                    })
                                );
                            return [
                                actions.UpdateSubscribedServices({
                                    ids: [action.resourceName],
                                }),
                                actions.GetAllApps({
                                    isPackagePage: false,
                                    considerGlobalRepository: isGlobalRepository,
                                    forceFetch: true,
                                }),
                            ];
                        }),
                        mergeMap(actionsArray => actionsArray),
                        catchError(res => {
                            return of(AlertError({ message: res }));
                        })
                    );
            })
        );
    });

    GetBrideAssetMetadataId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.GetBrideAssetMetadataId),
            withLatestFrom(this.store$.select(getBridgeIdsVsMetadataIds$)),
            mergeMap(([action, serviceIdVsAssetId]) => {
                if (serviceIdVsAssetId?.[action.serviceId]) {
                    return [];
                }
                return from(this._api.assets.getAssetWithLatestVersion({ serviceId: action.serviceId })).pipe(
                    map((response: any) => {
                        return actions.SetBridgeServiceIdVsAssetId({
                            serviceId: action.serviceId,
                            assetId: response.metadataId,
                        });
                    }),
                    catchError(e => of(AlertError({ message: e })))
                );
            })
        )
    );

    unsubscribeFromApp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.UnsubscribeFromApp),
            mergeMap(action => {
                return this._api.subscriptions.unsubscribeFromService(action.subscriptionId).pipe(
                    map(message => actions.UnsubscribeFromAppSuccess({ subscriptionId: action.subscriptionId, message })),
                    catchError(() => of(AlertError({ message: translate('Failed to unsubscribe from the app') })))
                );
            })
        )
    );

    unsubscribeSuccess$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(actions.UnsubscribeFromAppSuccess),
                mergeMap(action => {
                    this.store$.dispatch(AlertSuccess({ message: translate(action.message) }));
                    return [];
                })
            ),
        { dispatch: false }
    );
}
